// src/contexts/ThemeModeContext.js
//
// Single source of truth for light/dark mode across the whole app. Previously every
// dashboard (Admin, Clinician, Radiologist, ReaderPortal, Login, Register, Settings)
// kept its own local mode state under different localStorage keys ('theme',
// 'clinicianTheme', 'radiologistTheme'), so toggling on one page never matched another.
// Pages may still define their own MUI design tokens/branding — they just all read the
// same `mode` from here now instead of tracking it themselves.
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { createCustomTheme } from '../theme/themeConfig';
import { useAuth } from './AuthContext';

const ThemeModeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export function ThemeModeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    // No explicit preference saved — match the OS setting
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Track whether the user has made an explicit choice (vs inheriting from OS)
  const [userChose, setUserChose] = useState(() => !!localStorage.getItem('theme'));

  useEffect(() => {
    localStorage.setItem('theme', mode);
    const isDark = mode === 'dark';
    const bg = isDark ? '#0A0A0A' : '#F8FAFC';
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.backgroundColor = bg;
    }
  }, [mode]);

  // Follow OS theme changes in real-time, but only if the user hasn't explicitly chosen
  useEffect(() => {
    if (userChose) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [userChose]);

  // Apply the logged-in user's saved theme preference ONLY IF user hasn't chosen locally
  useEffect(() => {
    if (localStorage.getItem('theme')) return; // Explicit local preference takes precedence
    const userTheme = user?.preferences?.theme;
    if (userTheme && userTheme !== mode) {
      setMode(userTheme);
    }
  }, [user?.preferences?.theme]);

  // Settings pages broadcast these events after saving — apply immediately everywhere
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      const theme = event.detail?.preferences?.theme;
      if (theme) {
        setUserChose(true);
        localStorage.setItem('theme', theme);
        setMode(theme);
      }
    };
    const handleThemeChanged = (event) => {
      const theme = event.detail?.theme;
      if (theme) {
        setUserChose(true);
        localStorage.setItem('theme', theme);
        setMode(theme);
      }
    };

    window.addEventListener('userSettingsUpdated', handleSettingsUpdate);
    window.addEventListener('themeChanged', handleThemeChanged);
    return () => {
      window.removeEventListener('userSettingsUpdated', handleSettingsUpdate);
      window.removeEventListener('themeChanged', handleThemeChanged);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setUserChose(true);
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', next);
      import('../services/api').then(({ default: api }) => {
        api.patch('/users/preferences', { darkMode: next === 'dark', theme: next }).catch(() => {});
      });
      return next;
    });
  }, []);

  const theme = useMemo(() => createTheme(createCustomTheme(mode)), [mode]);
  const value = useMemo(() => ({ mode, setMode, toggleTheme }), [mode, toggleTheme]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default ThemeModeContext;
